import { CITY } from '@city'
import { useContext, useEffect, useMemo, useRef, type ReactElement, type ReactNode } from 'react'
import { RoutingError, buildOnlineRoutingClient } from '@metro/routing'
import { ROUTING_BASE_URL } from '../env'
import { useSchema } from '../components/useSchema'
import { buildAdapter, type RoutingAdapter } from './routingAdapter'
import { createWorkerOfflineSearchRunner } from './workerOfflineSearchRunner'
import { MetroMapStoreContext } from './metroMapStoreContext'
import { useMetroMapDispatch } from './useMetroMapStore'
import { useRouteSelection } from './useRouteSelection'
import { filterMapDataByMode } from './filterMapDataByMode'
import { useSyncExternalStore } from 'react'
import {
  consoleRoutingTelemetryReporter,
  type RoutingTelemetryEvent,
  type RoutingTelemetryReporter,
} from './routingTelemetryReporter'
import { RoutingAdapterContext } from './useRoutingAdapter'
import { RoutingTelemetryContext } from './useRoutingTelemetry'
import { realRoutingTelemetryReporter } from '../analytics/webAnalyticsReporter'
import { reportHealthError, HEALTH_ID } from '../analytics/appHealthEvents'
import {
  buildNightRoutingOptions,
  filterNightIneligibleRoutes,
  nightEligibleLineIds,
  plannedNightDeparture,
} from './nightTube'
import { resolveRouteCardClock } from './routeCardClock'

/** Subscribe shim used when `MetroMapStoreContext` is null (e.g. tests that
 * mount RoutingController directly without a store provider). The
 * one-time no-op listener never fires, so React falls back to a stable
 * `currentMode = 'normal'` snapshot. */
const noopSubscribe = (): (() => void) => () => undefined
/**
 * Headless component (renders only a context provider with `null`
 * children). Mount once at AppShell. Watches the `routeSelection`
 * slice of the MetroMap store. When BOTH `from` and `to` are populated
 * it dispatches the `RouteRequestStarted` → `RouteResultsReceived` /
 * `RouteRequestFailed` lifecycle. When either becomes null it
 * dispatches `RouteCleared`.
 *
 * Cancellation: each request bumps a monotonic ref-tracked sequence
 * number; results from any but the latest sequence are dropped before
 * dispatch. This handles rapid From/To churn without leaking stale
 * routes into the store.
 *
 * `RoutingError('NoPath' | 'SameOriginDestination')` is mapped to
 * `RouteResultsReceived { routes: [] }` (which the reducer collapses
 * to `kind: 'empty'`). Any other thrown error becomes
 * `RouteRequestFailed`.
 */
export interface RoutingControllerProps {
  /**
   * Subtree that may consume the adapter via {@link useRoutingAdapter}.
   * Wrap every component that needs `lineLookup` (RouteCard mount sites)
   * or builds map overlays (MetroMapCanvas) inside this controller —
   * `useContext` only sees a Provider's descendants, so siblings of the
   * controller would silently see `null`.
   */
  readonly children?: ReactNode
}

/**
 * Loading-state timeout (ms). After this many ms in `loading` without
 * a result, we surface an error so the user can retry — covers the
 * background→foreground case where the in-flight request silently
 * died (tab thrown away, fetch cancelled by the system) and would
 * otherwise leave the spinner stuck forever (G-36 in audit).
 */
const LOADING_TIMEOUT_MS = 15_000

export function RoutingController({ children }: RoutingControllerProps = {}): ReactElement {
  const schema = useSchema()
  const routeSelection = useRouteSelection()
  const dispatch = useMetroMapDispatch()

  // Online client is constructed once per page load — the wire format
  // is stable, the base URL is build-time configurable. Reusing one
  // instance across adapter rebuilds (e.g. schema swap during dev HMR)
  // avoids leaking AbortControllers + lets the LRU cache survive a
  // routing-graph reshape since the cache lives on the adapter itself.
  // A city without online routing (NYC, no TfL) routes offline only.
  const onlineClient = useMemo(
    () => (CITY.onlineRouting ? buildOnlineRoutingClient(ROUTING_BASE_URL) : undefined),
    [],
  )

  // Production emits the routing telemetry to GA4 +
  // Amplitude via `realRoutingTelemetryReporter` (no-op outside
  // production). The dev-only console reporter is also chained so local
  // builds keep a visible event stream — the two are mutually exclusive
  // by env (console no-ops in prod, real no-ops in dev), so chaining them
  // is release-path symmetric.
  const telemetry = useMemo<RoutingTelemetryReporter>(
    () => (event: RoutingTelemetryEvent) => {
      consoleRoutingTelemetryReporter(event)
      realRoutingTelemetryReporter(event)
    },
    [],
  )

  // Night Tube route hygiene fix (cross-platform decision #2):
  // pre-filter `schema` by the active Night Tube mode before handing it to
  // the routing graph. In `normal` mode this drops night-only stations /
  // connections — V1 iOS leaks them into the pathfinder, producing routes
  // that traverse closed stations and render with missing geometry. iOS V2
  // and Android land the same fix on their side; this is intentional drift
  // from V1 parity. See [[project_metro_night_tube_implementation]].
  const storeForMode = useContext(MetroMapStoreContext)
  const currentMode = useSyncExternalStore(
    storeForMode?.subscribe ?? noopSubscribe,
    () => storeForMode?.getState().currentMode ?? 'normal',
  )
  const filteredSchema = useMemo(() => {
    if (!schema) return null
    return filterMapDataByMode(schema, currentMode)
  }, [schema, currentMode])
  // Lines the night-mode safety net keeps — read from the (unfiltered) map
  // data so both editions' ids are known (`nightEligibleLineIds`).
  const nightLineIds = useMemo<ReadonlySet<string>>(
    () => (schema ? nightEligibleLineIds(schema) : new Set()),
    [schema],
  )

  const adapter = useMemo<RoutingAdapter | null>(() => {
    if (!filteredSchema) return null
    try {
      return buildAdapter(filteredSchema, {
        onlineClient,
        telemetry,
        // Off the main thread where the browser has workers (TASK-986): an
        // offline search freezes the page for its whole duration.
        offlineSearchRunner:
          typeof Worker === 'undefined' ? undefined : createWorkerOfflineSearchRunner,
        // Complexes from the unfiltered map: at night a picked stop on a line
        // that does not run falls back to a running sibling.
        complexStations: schema?.stations,
      })
    } catch (err: unknown) {
      // §3 `routing_graph_load_failed` — the routing GRAPH failed to build
      // (schema-derived graph construction error). Distinct from a route that
      // legitimately can't be built (that throws RoutingError at findRoutes →
      // the `route_resolve_failed` product event, NOT health). `error`:
      // routing is unavailable until the schema/graph is fixed. Returning null
      // keeps the existing offline-only / disabled behaviour unchanged.
      reportHealthError(
        HEALTH_ID.routingGraphLoadFailed,
        `routing graph build failed: ${String(err)}`,
        {
          domain: 'routing',
          severity: 'error',
          errorKind: 'parse',
        },
      )
      return null
    }
  }, [filteredSchema, schema, onlineClient, telemetry])
  // A replaced adapter (night mode, new schema) releases its worker.
  useEffect(() => () => adapter?.dispose(), [adapter])

  // Per-request AbortController so that:
  //  - G-10: a fresh From/To change aborts the prior fetch instead of
  //    waiting up to 3 s for it to settle (seqRef-only model wasted
  //    bandwidth + battery + blocked the next request behind the gate).
  //  - G-11: clearing From/To (route dismiss) aborts the in-flight
  //    request AND bumps seqRef so the eventual resolution doesn't
  //    resurrect a `results` state on top of `collapsed`.
  const seqRef = useRef(0)
  const inFlightAbortRef = useRef<AbortController | null>(null)
  const supersede = (): void => {
    seqRef.current++
    const ctrl = inFlightAbortRef.current
    if (ctrl !== null) {
      ctrl.abort()
      inFlightAbortRef.current = null
    }
  }

  const fromId = routeSelection.fromStation?.id
  const toId = routeSelection.toStation?.id
  // Hoisted above the lifecycle effect so the dismiss-emit branch can
  // read the in-flight `routeStatus` synchronously without re-rendering.
  const store = useContext(MetroMapStoreContext)

  // Retry hook for the loading-timeout + visibility-resume paths. Kept
  // in a ref so the visibilitychange listener (registered once) can
  // re-fire the request without re-running the entire effect chain.
  const adapterRef = useRef<RoutingAdapter | null>(adapter)
  const fromIdRef = useRef<string | undefined>(fromId)
  const toIdRef = useRef<string | undefined>(toId)
  // Night-mode ref so `runRequest` (also fired from the visibilitychange
  // listener registered once) always reads the current mode when building
  // the online `depart_at` + applying the night safety-net filter.
  const isNightMode = currentMode === 'night'
  const nightModeRef = useRef(isNightMode)

  // Keep the four "latest value" mirrors current. These used to be assigned in
  // the render body, which `react-hooks/refs` forbids: a render can be thrown
  // away or replayed, so a write there is not guaranteed to correspond to a
  // committed UI.
  //
  // DECLARATION ORDER IS LOAD-BEARING — this effect MUST stay the first effect
  // in the component. React flushes effects in declaration order within a
  // commit, and the lifecycle effect below calls `runRequest`, which reads
  // `nightModeRef.current` synchronously. Move this lower and that read goes
  // one render stale. No dependency array: every committed render re-syncs.
  useEffect(() => {
    adapterRef.current = adapter
    fromIdRef.current = fromId
    toIdRef.current = toId
    nightModeRef.current = isNightMode
  })

  const runRequest = (fId: string, tId: string, a: RoutingAdapter): void => {
    supersede()
    const seq = ++seqRef.current
    const ctrl = new AbortController()
    inFlightAbortRef.current = ctrl
    dispatch({ kind: 'RouteRequestStarted' })
    void (async () => {
      try {
        // Night Tube fix (a): in night mode the online request carries a
        // `depart_at` at the next Night-Tube window so the backend returns
        // real night services rather than the day timetable. Normal mode
        // passes `undefined` (depart-now, unchanged). Mirrors iOS
        // `NightTube.nextNightTubeDeparture` → `OnlineRoutingOptions.departAt`.
        // TODO(G-4): also plumb accessibility-pref / arriveAt here once a
        // RouteCard UI control exists.
        const night = nightModeRef.current
        // The route card's clock seam doubles as "now" so a frozen test clock
        // plans the same night window every run.
        const now = resolveRouteCardClock()?.() ?? new Date()
        const options = buildNightRoutingOptions(night, now)
        const departsAt = plannedNightDeparture(night, now)
        const routes = await a.findRoutes(fId, tId, options, ctrl.signal)
        if (seq !== seqRef.current) return
        // Night Tube fix (a) safety net: drop any route riding a
        // non-night-eligible line (a stray day line or phantom line code an
        // upstream leaked into a night result). No-op in normal mode.
        // The routes leave at the planned night departure, so their times
        // count from it (`routeTimesClock`), not from now.
        dispatch({
          kind: 'RouteResultsReceived',
          routes: filterNightIneligibleRoutes(routes, night, nightLineIds),
          ...(departsAt === undefined ? {} : { departsAt: departsAt.getTime() }),
        })
      } catch (e) {
        if (seq !== seqRef.current) return
        if (e instanceof RoutingError) {
          // Both 'NoPath' and 'SameOriginDestination' surface to the
          // user as "no routes" — same UX, no need to distinguish.
          dispatch({ kind: 'RouteResultsReceived', routes: [] })
        } else {
          const message = e instanceof Error ? e.message : 'Unknown error'
          dispatch({ kind: 'RouteRequestFailed', message })
        }
      } finally {
        if (inFlightAbortRef.current === ctrl) {
          inFlightAbortRef.current = null
        }
      }
    })()
  }

  useEffect(() => {
    if (!adapter || !fromId || !toId) {
      // G-11: dismissal mid-resolve. Bump seq + abort so the in-flight
      // request's resolution can't post `RouteResultsReceived` on top
      // of the just-issued `RouteCleared` (which the reducer collapses
      // back to `kind: 'collapsed'`).
      supersede()
      // Mirror iOS `cleared()` — emit `route_card_dismissed` only when
      // the card was actually showing something the user could be
      // dismissing (results / loading / empty / error). A no-op
      // collapse (already collapsed, no in-flight request) is the
      // initial mount path, not a user-driven dismiss.
      if (store !== null) {
        const status = store.getState().routeStatus
        if (status.kind !== 'collapsed') {
          const routeTotal = status.kind === 'results' ? status.routes.length : 0
          const viewedVariantIndex = status.kind === 'results' ? status.selectedIndex : 0
          // Last-known from/to is the wire contract: at dismiss time one
          // of them is already null, but the analytics dashboard expects
          // a stable shape — substitute empty string when absent.
          telemetry({
            kind: 'cardDismissed',
            fromStation: routeSelection.fromStation?.id ?? '',
            toStation: routeSelection.toStation?.id ?? '',
            routeTotal,
            viewedVariantIndex,
          })
        }
      }
      dispatch({ kind: 'RouteCleared' })
      return
    }
    runRequest(fromId, toId, adapter)
    // We deliberately omit `runRequest`/`supersede` from deps — they
    // close over refs that update in place, and adding them would
    // re-fire on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, fromId, toId, dispatch])

  // G-36: loading-timeout guard. If we sit in `.loading` for longer
  // than LOADING_TIMEOUT_MS without the in-flight request resolving,
  // surface an error so the user can retry. Covers the wedged-after-
  // background case where the system killed the fetch but no callback
  // fired. (`store` is hoisted earlier in the component scope so the
  // dismiss-emit branch can read `routeStatus` synchronously.)
  useEffect(() => {
    if (store === null) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const arm = (): void => {
      if (timer !== null) clearTimeout(timer)
      timer = setTimeout(() => {
        const s = store.getState()
        if (s.routeStatus.kind === 'loading') {
          supersede()
          dispatch({
            kind: 'RouteRequestFailed',
            message: 'Request timed out. Tap to retry.',
          })
        }
      }, LOADING_TIMEOUT_MS)
    }
    const disarm = (): void => {
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
    }
    const unsubscribe = store.subscribe(() => {
      const s = store.getState()
      if (s.routeStatus.kind === 'loading') arm()
      else disarm()
    })
    // Arm immediately for current state.
    if (store.getState().routeStatus.kind === 'loading') arm()
    return () => {
      unsubscribe()
      disarm()
    }
  }, [store, dispatch])

  // G-36: visibility resume. When the tab becomes visible again and
  // we're stuck in `.loading` without an in-flight request (the system
  // killed the fetch during backgrounding), re-fire the request so
  // the user doesn't have to manually retry.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const onVisibility = (): void => {
      if (document.visibilityState !== 'visible') return
      if (store === null) return
      if (store.getState().routeStatus.kind !== 'loading') return
      if (inFlightAbortRef.current !== null) return
      const a = adapterRef.current
      const f = fromIdRef.current
      const t = toIdRef.current
      if (a === null || f === undefined || t === undefined) return
      runRequest(f, t, a)
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store])

  return (
    <RoutingAdapterContext.Provider value={adapter}>
      <RoutingTelemetryContext.Provider value={telemetry}>
        {children}
      </RoutingTelemetryContext.Provider>
    </RoutingAdapterContext.Provider>
  )
}
